import { handleSessionEnded } from '@wehunt/core-web'
import { ApiError, type ProblemDetails } from './ApiError'
import { parseRetryAfterSeconds } from './retryAfter'

// All API calls go through nginx's /api proxy (see apps/wehunt-web/nginx.conf.template),
// which forwards to the API based on the runtime NGINX_BACKEND_URL env var. The bundle
// itself has no environment-specific URL baked in.
const BASE_URL = '/api'

export interface ApiFetchInit extends Omit<RequestInit, 'headers'> {
  token: string
  headers?: Record<string, string>
}

/**
 * The server's error body, or an empty one when there is nothing readable.
 *
 * Reading it is what lets a caller tell one 401 apart from another: the API
 * answers `{ error, code }`, and the single-logout reaction keys on `code`. A
 * body that is not json (an nginx error page, an empty 502) must not turn into a
 * parse failure that hides the status the caller actually needs.
 */
async function readProblem(res: Response): Promise<ProblemDetails> {
  try {
    const body = (await res.json()) as unknown
    return typeof body === 'object' && body !== null ? (body as ProblemDetails) : {}
  } catch {
    return {}
  }
}

async function request(path: string, init: ApiFetchInit): Promise<Response> {
  const { token, headers, ...rest } = init
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) {
    // A real Response always carries `headers`; the optional chain only softens
    // hand-built fakes, and the cost is that a fake which omits them silently
    // exercises the backoff fallback instead of failing loudly.
    const error = new ApiError(
      res.status,
      await readProblem(res),
      parseRetryAfterSeconds(res.headers?.get('retry-after')),
    )
    // Wired here rather than in useApiFetch so that EVERY caller is covered: about
    // thirty call sites reach apiFetch or apiFetchBlob directly (the teams api
    // modules, the replay hook, and so on), and a reaction living only in the hook
    // left all of them holding a credential whose sso session had ended.
    handleSessionEnded(error)
    throw error
  }
  return res
}

export async function apiFetch<T>(path: string, init: ApiFetchInit): Promise<T> {
  const res = await request(path, init)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/**
 * Same request and same error translation as `apiFetch`, but reads the response
 * as bytes instead of JSON — for endpoints that serve a file rather than a
 * document shape.
 *
 * Reading a binary body through the API at all is the point: `/api` is
 * same-origin to the SPA (nginx proxies it), so the bytes arrive without a CORS
 * negotiation and can be handed to a download under the file's own name, which a
 * cross-origin storage URL cannot do.
 */
export async function apiFetchBlob(path: string, init: ApiFetchInit): Promise<Blob> {
  const res = await request(path, init)
  return res.blob()
}
